/**
 * Which anchor clicks this SPA handles itself, and which the browser keeps.
 *
 * Every internal link on this site is a plain `<a href="/...">` — the brand
 * logo, the header, the footer, the cross-product hand-offs, the links
 * interpolated into translated copy. None of them was a router link, so every
 * one was a full document load: ~2.1MB of vendored jQuery and Webflow
 * re-downloaded, re-parsed and re-executed, React rebooted, to move between two
 * pages already sitting in the same bundle.
 *
 * A delegated click handler covers all of them at once (see
 * `useInternalLinkNavigation`), which is why this is a predicate over a click
 * rather than a link component. Two of the call sites cannot be a component at
 * all: the cookie banner's privacy link is interpolated by `<Trans>`, and guide
 * body links are built inside `GuideArticle`. A component would also be opt-in,
 * so the next `<a href="/...">` anyone adds would silently be a page load
 * again.
 *
 * This module is pure — no DOM, no router — so `internalNavigation.test.mjs`
 * can exercise the decision table directly.
 */

import { stripLanguagePrefix } from "./language.js";

/**
 * Every path `App.jsx` routes. An allowlist, not a "does it start with /"
 * check: the catch-all route renders Home for *any* unmatched path, so without
 * this a click on `/sitemap.xml` or a PDF would be swallowed into the homepage
 * instead of being fetched from the server.
 *
 * `home.seo.test.mjs` asserts this stays in step with `App.jsx`.
 */
export const ROUTES = new Set([
  "/",
  "/home",
  "/contact",
  "/terms",
  "/privacy-policy",
  "/cookie-policy",
  "/guides",
  "/wedding-photo-sharing",
  "/digital-wedding-invitation",
  "/wedding-planning",
  "/wedding-rsvp",
  "/wedding-seating",
]);

/** `/guides/:slug` — the one parameterised route. */
const GUIDE_PATH = /^\/guides\/[a-z0-9-]+$/;

/**
 * True for a path this app renders itself, in either language tree.
 *
 * The language prefix is stripped before the lookup, so `ROUTES` stays the one
 * list of pages and does not need a mirrored `/el/...` half that could drift
 * out of step with it. `/el/wedding-planning` is routable because
 * `/wedding-planning` is.
 *
 * Exactly one prefix is stripped, so `/el/el/wedding-planning` normalises to
 * `/el/wedding-planning`, matches nothing, and is left to the server — a
 * doubled prefix must not be quietly repaired into a working page.
 *
 * Whether the Greek version of a routable page actually EXISTS is a different
 * question, answered by the page registry when the route renders (see
 * `App.jsx`). Claiming the click here either way is right: the app owns the
 * URL, and either renders it or redirects to the English page, both without a
 * full document load.
 */
export const isRoutablePath = (pathname) => {
  const base = stripLanguagePrefix(pathname);
  return ROUTES.has(base) || GUIDE_PATH.test(base);
};

/**
 * The in-app destination for a click, or `null` to let the browser do exactly
 * what it did before this module existed.
 *
 * @param {MouseEvent} event   the click
 * @param {HTMLAnchorElement} anchor  the anchor it landed on
 * @param {string} origin      `window.location.origin`
 * @param {string} currentPathname  the path being viewed, passed in rather than
 *   read off `window` so this stays pure and directly testable
 * @returns {{pathname: string, hash: string, search: string} | null}
 */
export function internalTargetFor(event, anchor, origin, currentPathname) {
  // Something nearer the link already handled it: the home-section scrollers,
  // the brand logo's scroll-to-top, the cookie banner's `href="#"` buttons.
  // React dispatches the deeper handler first, so by the time the delegate on
  // the app wrapper runs, this flag is already set. Deterministic, not a guess.
  if (event.defaultPrevented) return null;

  // Open-in-new-tab / new-window / download gestures belong to the browser.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return null;
  }
  if (typeof event.button === "number" && event.button !== 0) return null;

  if (!anchor) return null;
  if (anchor.hasAttribute("download")) return null;

  // `target="_blank"` on an *internal* path is real here — the pricing card
  // links to /terms in a new tab — so the target has to be checked before the
  // path, not instead of it.
  const target = anchor.getAttribute("target");
  if (target && target !== "_self") return null;

  const rel = (anchor.getAttribute("rel") || "").toLowerCase();
  if (rel.split(/\s+/).includes("external")) return null;

  const href = anchor.getAttribute("href");
  if (!href) return null;

  let url;
  try {
    url = new URL(anchor.href, origin);
  } catch {
    return null;
  }

  // Everything off-origin: the app CTAs on VITE_APP_URL, mailto:, tel:, social.
  if (url.origin !== origin) return null;

  if (!isRoutablePath(url.pathname)) return null;

  // A fragment on the page we are already on is a scroll, not a navigation.
  if (url.pathname === currentPathname && url.hash) return null;

  return { pathname: url.pathname, hash: url.hash, search: url.search };
}
